import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from '../lib/api.js';

/**
 * A row of the master "Required documents & details" checklist (Brief 9a). ONE
 * platform-level list — no tenant_id — so an edit here flows to every branch's
 * next send. This admin list includes INACTIVE rows (the send path filters to
 * active-only); the editor needs them so a disabled item can be re-enabled.
 */
export type ChecklistItem = {
  id: string;
  section: string;
  label: string;
  sortOrder: number;
  applicability: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
};

const LIST_KEY = ['admin', 'checklist'] as const;

export function useChecklist() {
  return useQuery({
    queryKey: LIST_KEY,
    queryFn: () => apiGet<{ items: ChecklistItem[] }>('/admin/checklist'),
    staleTime: 30_000,
  });
}

export function useAddChecklistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (label: string) => apiPost<{ item: ChecklistItem }>('/admin/checklist', { label }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
    },
  });
}

export function useEditChecklistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, label }: { id: string; label: string }) =>
      apiPatch<{ item: ChecklistItem }>(`/admin/checklist/${id}`, { label }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
    },
  });
}

export function useSetChecklistItemActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      apiPatch<{ item: ChecklistItem }>(`/admin/checklist/${id}`, { active }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
    },
  });
}
